import { ref } from "vue";
import { defineStore } from "pinia";

export interface DeliveryAddress {
  line_1?: string;
  line_2?: string;
  city?: string;
  postcode?: string;
  lat?: number;
  lng?: number;
  [key: string]: unknown;
}

export interface DeliveryFilter {
  address?: DeliveryAddress | false;
  date?: string;
  search?: string;
  tags?: unknown[];
  cuisines?: unknown[];
  allergens?: unknown[];
}

export const useDeliveryStore = defineStore("delivery", () => {
  // --------------- State ---------------
  const deliveryAddress = ref<DeliveryAddress | false>(false);
  const deliveryFilter = ref<DeliveryFilter>({
    address: false,
    date: "",
    search: "",
    tags: [],
    cuisines: [],
    allergens: [],
  });
  const deliveryWindows = ref<unknown[] | false>(false);
  const deliveryLoading = ref(false);

  // --------------- Actions ---------------
  function setDeliveryAddress(payload: DeliveryAddress | false) {
    deliveryAddress.value = payload;
    if (payload && import.meta.client) {
      localStorage.setItem("address", JSON.stringify(payload));
    }
  }

  function setDeliveryFilter(payload: Partial<DeliveryFilter>) {
    deliveryFilter.value = { ...deliveryFilter.value, ...payload };
  }

  function clearDeliveryFilter() {
    deliveryFilter.value = {
      address: deliveryAddress.value || false,
      date: "",
      search: "",
      tags: [],
      cuisines: [],
      allergens: [],
    };
  }

  function restoreDeliveryAddress() {
    if (import.meta.client) {
      const stored = localStorage.getItem("address");
      if (stored) {
        try {
          deliveryAddress.value = JSON.parse(stored) as DeliveryAddress;
          deliveryFilter.value.address = deliveryAddress.value;
        } catch {
          // Invalid stored address
        }
      }
    }
  }

  async function getDeliveryWindows(makerId: string | number) {
    const config = useRuntimeConfig();
    deliveryLoading.value = true;

    try {
      const response = await $fetch<unknown[]>(
        `${config.public.apiBase}/api/public/makers/deliveryWindows/${makerId}`,
      );
      deliveryWindows.value = response;
      deliveryLoading.value = false;
    } catch {
      deliveryLoading.value = false;
    }
  }

  return {
    deliveryAddress,
    deliveryFilter,
    deliveryWindows,
    deliveryLoading,

    setDeliveryAddress,
    setDeliveryFilter,
    clearDeliveryFilter,
    restoreDeliveryAddress,
    getDeliveryWindows,
  };
});
